import { useCallback, useState, useEffect } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
  TablePagination,
  Checkbox,
} from "@mui/material";

import {
  ButtonV1,
  Loader,
  PaginationAction,
  TooltipLabel,
  HyperLink,
  PriorityStatusChip,
} from "components";
import { Filters, ButtonView } from "./components";
import { ErrorView, QuickFilter, TooltipView } from "../components";

import { useToaster } from "hooks";

import {
  decimalRoundOff,
  moneyFormat,
  validateErrorMsg,
  validateNull,
} from "utils";
import { postCall_v2, putCall_v2 } from "services";

import { error, success } from "constants";
import {
  QUICK_FILTERS,
  PURCHASE_BILL_HEADERS,
  BULK_SHIPMENT_CATEGORY,
  PURCHASE_BILL_QUICK_FILTERS,
} from "../constant";

const rowsPerPage = 50;

const PuchaseBill = () => {
  const [purchaseBillData, setPurchaseBillData] = useState({});
  const [filters, setFilters] = useState({});
  const [selectedQuickFilter, setSelectedQuickFilter] = useState("Have error");
  const [page, setPage] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPurchaseBills, setSelectedPurchaseBills] = useState([]);
  const [isMarkAllChecked, setIsMarkAllChecked] = useState(false);

  const triggerToaster = useToaster();
  const theme = useTheme();

  const fetchBulkShipments = useCallback(async () => {
    try {
      const { data } = await postCall_v2(
        `/joms/api/external/v1/bulk-shipment/fetch-uploaded-shipment`,
        {
          offset: page * rowsPerPage,
          limit: rowsPerPage,
          filterDto: {
            bulkShipmentCategory: BULK_SHIPMENT_CATEGORY.PO,
            invoiceNumber: filters?.invoiceNumber || null,
            customerGstin: filters?.customerGSTIN || null,
            orderNumber: filters?.orderNumber || null,
            plantCode: filters?.plantCode || null,
            soNumber: filters?.soNumber || null,
            soLineItemId: filters?.soLineItem || null,
            sapShipmentNumber: filters?.shipmentNumber || null,
            status: filters?.status || null,
            quickFilter: QUICK_FILTERS?.[selectedQuickFilter],
            poNumber: filters?.poNumber || null,
            orderPriority: filters?.orderPriority || null,
          },
        },
      );

      if (data) {
        setPurchaseBillData(data);
      }
    } catch (err) {
      triggerToaster(err?.data?.errorMessage || "Something went wrong!", error);
    } finally {
      setIsLoading(false);
    }
  }, [page, filters, selectedQuickFilter]);

  useEffect(() => {
    setIsLoading(true);
    fetchBulkShipments();
  }, [fetchBulkShipments]);

  const handlePageChange = (_event, newPage) => setPage(newPage);
  const handleFilterSubmit = setFilters;
  const handleQuickFilter = setSelectedQuickFilter;

  const handleAllCheckBox = useCallback(
    (event) => {
      const isChecked = event.target.checked;
      setIsMarkAllChecked(isChecked);
      if (isChecked) {
        const allShipmentIds =
          purchaseBillData?.shipmentOrderLineitemAggregateDtoList?.map(
            (row) => row?.id,
          ) || [];
        setSelectedPurchaseBills(allShipmentIds);
      } else {
        setSelectedPurchaseBills([]);
      }
    },
    [purchaseBillData],
  );

  const handleCheckBox = useCallback(
    (row) => {
      setSelectedPurchaseBills((prev) => {
        const isSelected = prev.includes(row?.id);
        const newSelected = isSelected
          ? prev.filter((id) => id !== row?.id)
          : [...prev, row?.id];
        setIsMarkAllChecked(
          newSelected.length ===
          purchaseBillData?.shipmentOrderLineitemAggregateDtoList?.length,
        );
        return newSelected;
      });
    },
    [purchaseBillData],
  );

  const handleRefresh = async (shipmentIdList) => {
    try {
      const payload = {
        shipmentOrderLineItemAggregateIds: shipmentIdList,
      };
      await postCall_v2(
        "/joms/api/external/v1/bulk/sap/upload/refresh",
        payload,
      );
      setSelectedPurchaseBills([]);
      fetchBulkShipments();
      setIsMarkAllChecked(false);
      triggerToaster("Successfully refreshed details", success);
    } catch (err) {
      triggerToaster(
        err?.data?.errorMessage || "Please try again later",
        error,
      );
    }
  };

  const shipmentApproval = async (itemDetails, action) => {
    try {
      const actionMapper = {
        CREATE: "CREATE",
        REJECT: "REJECTED",
      };
      const payload = {
        shipmentLineItemAggregateId: itemDetails.id,
        status: actionMapper[action],
      };
      await postCall_v2(
        "/joms/api/external/v1/bulk/sap/change-status",
        payload,
      );
      triggerToaster(
        `Shipment is successfully ${actionMapper[action].toLowerCase()}`,
        success,
      );
      fetchBulkShipments();
    } catch (err) {
      triggerToaster(validateErrorMsg(err?.data), error);
    }
  };

  const handleResync = async (row) => {
    const data = await putCall_v2(
      `/joms/api/external/v1/bulk-shipment/retrigger-rejected-shipment/${row?.id}`,
    );
    if (data?.isSuccess) {
      triggerToaster("Completed successfully", success);
      fetchBulkShipments();
    } else {
      triggerToaster("Please try again", error);
    }
  };

  const purchaseBillList =
    purchaseBillData?.shipmentOrderLineitemAggregateDtoList;

  const isRefreshEnabled = selectedPurchaseBills.length > 0;

  return (
    <Box>
      <Box mb={4}>
        <Filters onFilterSubmit={handleFilterSubmit} />
      </Box>

      <Box
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <QuickFilter
          onQuickFilter={handleQuickFilter}
          state={selectedQuickFilter}
          filterData={PURCHASE_BILL_QUICK_FILTERS}
        />
        <ButtonV1
          title="Refresh for all errors"
          disabled={!isRefreshEnabled && !selectedPurchaseBills.length}
          onClick={() => handleRefresh(selectedPurchaseBills)}
        />
      </Box>
      <Box mt={4}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <Checkbox
                    onChange={handleAllCheckBox}
                    checked={isMarkAllChecked}
                  />
                </TableCell>
                {PURCHASE_BILL_HEADERS.map((header) => (
                  <TableCell key={header}>
                    <Typography fontSize={14} fontWeight="bold">
                      {header}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={PURCHASE_BILL_HEADERS.length + 2}>
                    <Loader />
                  </TableCell>
                </TableRow>
              )}
              {(purchaseBillList || []).map((row, index) => {
                const errors = row?.validationErrorMsg || [];

                if (row?.shipmentCreationErrorMsg) {
                  errors.push(row?.shipmentCreationErrorMsg);
                }
                const sapShipmentDetailListData =
                  row?.sapShipmentDetailList?.sapShipmentDetailList;

                const invoiceValue = sapShipmentDetailListData?.reduce(
                  (total, item) => {
                    return (
                      total +
                      +item?.quantity *
                      (+item?.lineItemUnitPriceWithTax?.amount +
                        +item?.lineItemUnitPriceWithTax?.taxAmount)
                    );
                  },
                  0,
                );

                const bgColor =
                  index % 2 === 0
                    ? theme.palette.common.white
                    : theme.palette.bgColor;

                return (
                  <>
                    <TableRow key={row?.orderNumber} style={{ backgroundColor: bgColor }}>
                      <TableCell>
                        {!!errors?.length && (
                          <Checkbox
                            checked={selectedPurchaseBills.includes(row?.id)}
                            onChange={() => handleCheckBox(row)}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <HyperLink title={row?.orderNumber} />
                      </TableCell>
                      <TableCell>{validateNull(row?.poNumber)}</TableCell>
                      <TableCell>{validateNull(row?.buyerNameERP)}</TableCell>
                      <TableCell>
                        {validateNull(row?.sapCustomerName)}
                      </TableCell>
                      <TableCell>
                        <PriorityStatusChip priority={row?.orderPriority} />
                      </TableCell>
                      <TableCell>{validateNull(row?.invoiceNumber)}</TableCell>
                      <TableCell>
                        {moneyFormat(decimalRoundOff(invoiceValue))}
                      </TableCell>
                      <TableCell />
                      <TableCell />
                      <TableCell />
                      <TableCell>
                        <ButtonView
                          row={row}
                          errors={errors}
                          handleRefresh={handleRefresh}
                          shipmentApproval={shipmentApproval}
                          handleResync={handleResync}
                        />
                      </TableCell>
                    </TableRow>
                    {sapShipmentDetailListData?.length &&
                      sapShipmentDetailListData?.map((item, index) => {
                        const invoicedPrice =
                          item?.lineItemUnitPriceWithTax?.amount;

                        const tooltipData = {
                          soLine: item?.soLineItemNumbers.join(","),
                          pendingReleaseQty: item?.pendingReleasedQuantity,
                          invoicedPrice,
                        };

                        return (
                          <TableRow style={{ backgroundColor: bgColor }} key={index + invoicedPrice}>
                            <TableCell colSpan={7} />
                            <TableCell
                              style={{
                                borderTop: `1.5px solid ${theme.palette.grey["400"]}`,
                              }}
                            >
                              <TooltipLabel
                                customStyle={{ width: 400 }}
                                placement="bottom"
                                title={
                                  item.skuProduct && (
                                    <TooltipView data={tooltipData} />
                                  )
                                }
                                labelChildren={
                                  <Typography
                                    sx={{
                                      cursor: "pointer",
                                      color: theme.palette.primary.main,
                                      fontSize: 14,
                                    }}
                                  >
                                    {validateNull(item.skuProduct)}
                                  </Typography>
                                }
                              />
                            </TableCell>
                            <TableCell
                              style={{
                                borderTop: `1.5px solid ${theme.palette.grey["400"]}`,
                              }}
                            >
                              {validateNull(item.sapSkuProduct)}
                            </TableCell>
                            <TableCell
                              colSpan={4}
                              style={{
                                borderTop: `1.5px solid ${theme.palette.grey["400"]}`,
                              }}
                            >
                              {`${validateNull(
                                decimalRoundOff(item.quantity, 3),
                              )}
                              ${item?.measurementUnit}`}
                            </TableCell>
                          </TableRow>
                        );
                      })}

                    <ErrorView
                      errors={errors}
                      warning={row?.validationWarningMsg ?? ""}
                      colSpan={PURCHASE_BILL_HEADERS.length + 1}
                    />
                  </>
                );
              })}
              {!isLoading && !purchaseBillList?.length && (
                <TableRow>
                  <TableCell colSpan={PURCHASE_BILL_HEADERS.length + 2}>
                    <Typography textAlign={"center"}>No data found</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          {!!purchaseBillData && (
            <TablePagination
              rowsPerPageOptions={[rowsPerPage]}
              rowsPerPage={rowsPerPage}
              page={Math.ceil(purchaseBillData?.offset / rowsPerPage) ?? 0}
              count={purchaseBillData?.totalCount ?? 0}
              onPageChange={handlePageChange}
              sx={{ display: "flex", flexDirection: "column-reverse" }}
              ActionsComponent={PaginationAction}
            />
          )}
        </TableContainer>
      </Box>
    </Box>
  );
};

export default PuchaseBill;
